import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { onboardingApi } from '@/lib/onboarding';
import { Field, Section } from '../onboarding/components/fields';
import { btnGhost, btnPrimary, inputCls } from '../onboarding/components/styles';

/** Кабинет поставщика на выезде (этап 1.2б, шаг 1): минимум полей, остальное — в онбординге. */
export default function NewPartnerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', shortName: '', trn: '', contact: '', phone: '', whatsapp: '', email: '', city: '', warehouse: '', pickupHours: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v.trim()) body[k] = v.trim();
      });
      const p = await onboardingApi.createPartner(body);
      navigate(`/partners/${p.id}/onboarding`, { replace: true });
    } catch (err) {
      setError((err as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/partners" className="mb-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line text-[12px]" /> {t('pd.backToPartners')}
      </Link>
      <PageHeader eyebrow={t('onb.newPartnerEyebrow')} title={t('onb.newPartner')} description={t('onb.newPartnerDesc')} />
      <form onSubmit={submit} className="mx-auto flex max-w-3xl flex-col gap-4">
        <Section title={t('onb.sectionCompany')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={`${t('onb.name')} *`} className="sm:col-span-2">
              <input className={inputCls} value={form.name} onChange={set('name')} required autoFocus />
            </Field>
            <Field label={t('onb.shortName')}>
              <input className={inputCls} value={form.shortName} onChange={set('shortName')} />
            </Field>
            <Field label={t('onb.trn')}>
              <input className={`${inputCls} tnum`} value={form.trn} onChange={set('trn')} inputMode="numeric" placeholder="100000000000003" />
            </Field>
          </div>
        </Section>
        <Section title={t('onb.sectionContacts')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('onb.contact')}>
              <input className={inputCls} value={form.contact} onChange={set('contact')} />
            </Field>
            <Field label={t('onb.email')}>
              <input className={inputCls} type="email" value={form.email} onChange={set('email')} />
            </Field>
            <Field label={t('onb.phone')}>
              <input className={inputCls} value={form.phone} onChange={set('phone')} inputMode="tel" />
            </Field>
            <Field label={t('onb.whatsapp')}>
              <input className={inputCls} value={form.whatsapp} onChange={set('whatsapp')} inputMode="tel" />
            </Field>
          </div>
        </Section>
        <Section title={t('onb.sectionWarehouse')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('onb.city')}>
              <input className={inputCls} value={form.city} onChange={set('city')} />
            </Field>
            <Field label={t('onb.pickupHours')}>
              <input className={inputCls} value={form.pickupHours} onChange={set('pickupHours')} placeholder="Sat–Thu 9:00–18:00" />
            </Field>
            <Field label={t('onb.warehouse')} className="sm:col-span-2">
              <input className={inputCls} value={form.warehouse} onChange={set('warehouse')} />
            </Field>
          </div>
        </Section>
        <div className="flex items-center justify-end gap-2">
          <Link to="/partners" className={btnGhost}>
            {t('onb.cancel')}
          </Link>
          <button type="submit" disabled={busy || !form.name.trim()} className={btnPrimary}>
            <i className="ri-store-2-line text-[14px]" /> {busy ? t('onb.creating') : t('onb.create')}
          </button>
        </div>
      </form>
      {error && <Toast message={t('onb.error', { code: error })} tone="error" onClose={() => setError(null)} />}
    </div>
  );
}
